import React, { useState, useEffect } from 'react';
import './AdminOrders.css';
import { useToast } from '../../../context/ToastContext';
const AdminOrders = () => {
  const toast = useToast();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [showStatusModal, setShowStatusModal] = useState(false);
  const [statusChangeOrder, setStatusChangeOrder] = useState(null);
  const [filterStatus, setFilterStatus] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [sortBy, setSortBy] = useState('date-desc');

  useEffect(() => {
    fetchOrders();
  }, []);

  const fetchOrders = async () => {
    setLoading(true);
    try {
      const { db } = await import('../../../services/firebase/firebaseconfig');
      const { collection, getDocs, query, orderBy } = await import('firebase/firestore');

      const ordersRef = collection(db, 'orders');
      const q = query(ordersRef, orderBy('createdAt', 'desc'));
      const snapshot = await getDocs(q);

      const ordersData = snapshot.docs.map((doc, index) => ({
        id: doc.id,
        orderNumber: snapshot.docs.length - index, // Simple incrementing number
        ...doc.data()
      }));

      setOrders(ordersData);
    } catch (error) {
      console.error('Error fetching orders:', error);
      toast.showError('Erreur lors du chargement des commandes');
    } finally {
      setLoading(false);
    }
  };

  const openStatusChange = (order, fromModal = false) => {
    setStatusChangeOrder(order);
    setShowStatusModal(true);
    if (!fromModal) {
      setShowModal(false);
    }
  };

  const updateOrderStatus = async (newStatus) => {
    if (!statusChangeOrder) return;

    try {
      const { db } = await import('../../../services/firebase/firebaseconfig');
      const { doc, updateDoc } = await import('firebase/firestore');

      const orderRef = doc(db, 'orders', statusChangeOrder.id);
      await updateDoc(orderRef, {
        status: newStatus,
        updatedAt: new Date().toISOString()
      });

      // Update local state
      setOrders(prev => prev.map(order =>
        order.id === statusChangeOrder.id ? { ...order, status: newStatus } : order
      ));

      if (selectedOrder?.id === statusChangeOrder.id) {
        setSelectedOrder(prev => ({ ...prev, status: newStatus }));
      }

      setShowStatusModal(false);
      setStatusChangeOrder(null);
      toast.showSuccess('Statut mis à jour avec succès');
    } catch (error) {
      console.error('Error updating order:', error);
      toast.showError('Erreur lors de la mise à jour');
    }
  };

  const deleteOrder = async (orderId) => {
    if (!window.confirm('Êtes-vous sûr de vouloir supprimer cette commande ?')) {
      return;
    }

    try {
      const { db } = await import('../../../services/firebase/firebaseconfig');
      const { doc, deleteDoc } = await import('firebase/firestore');

      const orderRef = doc(db, 'orders', orderId);
      await deleteDoc(orderRef);

      setOrders(prev => prev.filter(order => order.id !== orderId));
      setShowModal(false);
      toast.showSuccess('Commande supprimée avec succès');
    } catch (error) {
      console.error('Error deleting order:', error);
      toast.showError('Erreur lors de la suppression');
    }
  };

  const viewOrderDetails = (order) => {
    setSelectedOrder(order);
    setShowModal(true);
  };

  const getStatusBadgeClass = (status) => {
    const statusMap = {
      'pending': 'status-pending',
      'confirmed': 'status-confirmed',
      'processing': 'status-processing',
      'shipped': 'status-shipped',
      'delivered': 'status-delivered',
      'cancelled': 'status-cancelled'
    };
    return statusMap[status] || 'status-pending';
  };

  const getStatusLabel = (status) => {
    const statusLabels = {
      'pending': 'En attente',
      'confirmed': 'Confirmée',
      'processing': 'En traitement',
      'shipped': 'Expédiée',
      'delivered': 'Livrée',
      'cancelled': 'Annulée'
    };
    return statusLabels[status] || status;
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('fr-FR', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatPrice = (price) => {
    return price?.toLocaleString('fr-FR') || '0';
  };

  const formatDimensions = (dimensions) => {
    if (!dimensions) return '';
    const parts = [];
    if (dimensions.width) parts.push(`L${dimensions.width}`);
    if (dimensions.height) parts.push(`H${dimensions.height}`);
    if (dimensions.length) parts.push(`P${dimensions.length}`);
    return parts.join(' × ') + ' cm';
  };

  // Get available status options (excluding current status)
  const getAvailableStatuses = (currentStatus) => {
    const allStatuses = [
      { value: 'pending', label: 'En attente' },
      { value: 'confirmed', label: 'Confirmée' },
      { value: 'processing', label: 'En traitement' },
      { value: 'shipped', label: 'Expédiée' },
      { value: 'delivered', label: 'Livrée' },
      { value: 'cancelled', label: 'Annulée' }
    ];
    return allStatuses.filter(status => status.value !== currentStatus);
  };

  // Sort and filter orders
  const getSortedAndFilteredOrders = () => {
    let filtered = orders.filter(order => {
      const matchesStatus = filterStatus === 'all' || order.status === filterStatus;
      const matchesSearch =
        order.fullName?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        order.orderNumber?.toString().includes(searchQuery) ||
        order.phone?.includes(searchQuery);
      return matchesStatus && matchesSearch;
    });

    // Sort
    const sorted = [...filtered].sort((a, b) => {
      switch (sortBy) {
        case 'date-desc':
          return new Date(b.createdAt) - new Date(a.createdAt);
        case 'date-asc':
          return new Date(a.createdAt) - new Date(b.createdAt);
        case 'price-desc':
          return (b.total || 0) - (a.total || 0);
        case 'price-asc':
          return (a.total || 0) - (b.total || 0);
        case 'status':
          return (a.status || '').localeCompare(b.status || '');
        default:
          return 0;
      }
    });

    return sorted;
  };

  const filteredOrders = getSortedAndFilteredOrders();

  // Statistics
  const stats = {
    total: orders.length,
    pending: orders.filter(o => o.status === 'pending').length,
    confirmed: orders.filter(o => o.status === 'confirmed').length,
    delivered: orders.filter(o => o.status === 'delivered').length,
    totalRevenue: orders
      .filter(o => o.status === 'delivered')
      .reduce((sum, o) => sum + (o.total || 0), 0)
  };

  return (
    <div className="admin-orders">
      {/* Header with Stats */}
      <div className="orders-header">
        <div className="header-content">
          <div className="quick-stats">
            <div className="quick-stat">
              <span className="quick-stat-value">{stats.total}</span>
              <span className="quick-stat-label">Total</span>
            </div>
            <div className="quick-stat pending">
              <span className="quick-stat-value">{stats.pending}</span>
              <span className="quick-stat-label">En attente</span>
            </div>
            <div className="quick-stat success">
              <span className="quick-stat-value">{stats.delivered}</span>
              <span className="quick-stat-label">Livrées</span>
            </div>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="orders-filters">
        <div className="filter-search">
          <svg className="search-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <circle cx="11" cy="11" r="8" />
            <path d="m21 21-4.35-4.35" />
          </svg>
          <input
            type="text"
            placeholder="Rechercher par nom, numéro, téléphone..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="search-input"
          />
        </div>

        <select
          value={filterStatus}
          onChange={(e) => setFilterStatus(e.target.value)}
          className="filter-select"
        >
          <option value="all">Tous les statuts</option>
          <option value="pending">En attente</option>
          <option value="confirmed">Confirmée</option>
          <option value="processing">En traitement</option>
          <option value="shipped">Expédiée</option>
          <option value="delivered">Livrée</option>
          <option value="cancelled">Annulée</option>
        </select>

        <select
          value={sortBy}
          onChange={(e) => setSortBy(e.target.value)}
          className="filter-select"
        >
          <option value="date-desc">Date (Plus récent)</option>
          <option value="date-asc">Date (Plus ancien)</option>
          <option value="price-desc">Prix (Plus élevé)</option>
          <option value="price-asc">Prix (Plus bas)</option>
          <option value="status">Statut</option>
        </select>

        <button className="btn btn-primary" onClick={fetchOrders}>
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <polyline points="23 4 23 10 17 10" />
            <polyline points="1 20 1 14 7 14" />
            <path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15" />
          </svg>
          Actualiser
        </button>
      </div>

      {/* Orders Table */}
      {loading ? (
        <div className="loading-state">
          <div className="spinner"></div>
          <p>Chargement des commandes...</p>
        </div>
      ) : filteredOrders.length === 0 ? (
        <div className="empty-state">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <circle cx="9" cy="21" r="1" />
            <circle cx="20" cy="21" r="1" />
            <path d="M1 1h4l2.68 13.39a2 2 0 0 0 2 1.61h9.72a2 2 0 0 0 2-1.61L23 6H6" />
          </svg>
          <h3>Aucune commande trouvée</h3>
          <p>Aucune commande ne correspond à vos critères de recherche</p>
        </div>
      ) : (
        <div className="orders-table-container">
          <table className="orders-table">
            <thead>
              <tr>
                <th>N°</th>
                <th>Client</th>
                <th>Produit</th>
                <th>Téléphone</th>
                <th>Total</th>
                <th>Date</th>
                <th>Statut</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredOrders.map((order, index) => (
                <tr key={order.id}>
                  <td>
                    <span className="order-number">#{index + 1}</span>
                  </td>
                  <td>
                    <div className="customer-info">
                      <div className="customer-name">{order.fullName}</div>
                      <div className="customer-location">{order.wilaya}</div>
                    </div>
                  </td>
                  <td>
                    <div className="product-info-cell">
                      {order.productImage && (
                        <img src={order.productImage} alt="" className="product-mini-thumb" />
                      )}
                      <div>
                        <div className="product-mini-name">{order.productName}</div>
                        {order.hasVariants && order.variantDetails && order.variantDetails.name && (
                          <div className="variant-mini-badge">
                            {order.variantDetails.name}
                          </div>
                        )}
                      </div>
                    </div>
                  </td>
                  <td>
                    <span className="phone-number">{order.phone}</span>
                  </td>
                  <td>
                    <span className="order-total">{formatPrice(order.total)} DA</span>
                  </td>
                  <td>
                    <span className="order-date">{formatDate(order.createdAt)}</span>
                  </td>
                  <td>
                    <span className={`status-badge ${getStatusBadgeClass(order.status)}`}>
                      {getStatusLabel(order.status)}
                    </span>
                  </td>
                  <td>
                    <div className="action-buttons">
                      <button
                        className="btn-icon btn-view"
                        onClick={() => viewOrderDetails(order)}
                        title="Voir détails"
                      >
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
                          <circle cx="12" cy="12" r="3" />
                        </svg>
                      </button>
                      <button
                        className="btn-icon btn-status"
                        onClick={() => openStatusChange(order, false)}
                        title="Changer le statut"
                      >
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <polyline points="22 12 18 12 15 21 9 3 6 12 2 12" />
                        </svg>
                      </button>
                      <button
                        className="btn-icon btn-delete"
                        onClick={() => deleteOrder(order.id)}
                        title="Supprimer"
                      >
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <polyline points="3 6 5 6 21 6" />
                          <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
                        </svg>
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Order Details Modal */}
      {showModal && selectedOrder && (
        <div className="modal-overlay" onClick={() => setShowModal(false)}>
          <div className="modal-content order-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Commande #{selectedOrder.orderNumber}</h2>
              <button className="modal-close" onClick={() => setShowModal(false)}>
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <line x1="18" y1="6" x2="6" y2="18" />
                  <line x1="6" y1="6" x2="18" y2="18" />
                </svg>
              </button>
            </div>

            <div className="modal-body">
              {/* Order Info */}
              <div className="detail-section">
                <h3 className="detail-title">Informations de commande</h3>
                <div className="detail-grid">
                  <div className="detail-item">
                    <span className="detail-label">Numéro:</span>
                    <span className="detail-value">#{selectedOrder.orderNumber}</span>
                  </div>
                  <div className="detail-item">
                    <span className="detail-label">Date:</span>
                    <span className="detail-value">{formatDate(selectedOrder.createdAt)}</span>
                  </div>
                  <div className="detail-item">
                    <span className="detail-label">Statut:</span>
                    <span className={`status-badge ${getStatusBadgeClass(selectedOrder.status)}`}>
                      {getStatusLabel(selectedOrder.status)}
                    </span>
                  </div>
                </div>
              </div>

              {/* Customer Info */}
              <div className="detail-section">
                <h3 className="detail-title">Informations client</h3>
                <div className="detail-grid">
                  <div className="detail-item">
                    <span className="detail-label">Nom:</span>
                    <span className="detail-value">{selectedOrder.fullName}</span>
                  </div>
                  <div className="detail-item">
                    <span className="detail-label">Téléphone:</span>
                    <span className="detail-value">{selectedOrder.phone}</span>
                  </div>
                  {selectedOrder.email && (
                    <div className="detail-item full-width">
                      <span className="detail-label">Email:</span>
                      <span className="detail-value">{selectedOrder.email}</span>
                    </div>
                  )}
                </div>
              </div>

              {/* Shipping Address */}
              <div className="detail-section">
                <h3 className="detail-title">Adresse de livraison</h3>
                <div className="detail-grid">
                  <div className="detail-item">
                    <span className="detail-label">Wilaya:</span>
                    <span className="detail-value">{selectedOrder.wilaya}</span>
                  </div>
                  <div className="detail-item">
                    <span className="detail-label">Commune:</span>
                    <span className="detail-value">{selectedOrder.commune}</span>
                  </div>
                  <div className="detail-item full-width">
                    <span className="detail-label">Adresse:</span>
                    <span className="detail-value">{selectedOrder.address}</span>
                  </div>
                </div>
              </div>

              {/* Product Details */}
              <div className="detail-section">
                <h3 className="detail-title">Produit commandé</h3>
                <div className="order-product-card">
                  <img src={selectedOrder.productImage} alt={selectedOrder.productName} />
                  <div className="order-product-info">
                    <h4>{selectedOrder.productName}</h4>
                    <p>Quantité: {selectedOrder.quantity}</p>
                    <p>Prix unitaire: {formatPrice(selectedOrder.productPrice)} DA</p>

                    {selectedOrder.hasVariants && selectedOrder.variantDetails && (
                      <div className="variant-details-box">
                        <strong>Variante sélectionnée:</strong>

                        {selectedOrder.variantDetails.name && (
                          <div className="variant-detail-item">
                            📦 <strong>Nom:</strong> {selectedOrder.variantDetails.name}
                          </div>
                        )}

                        {selectedOrder.variantDetails.dimensions && (
                          <div className="variant-detail-item">
                            📏 <strong>Dimensions:</strong> {formatDimensions(selectedOrder.variantDetails.dimensions)}
                          </div>
                        )}

                        {selectedOrder.variantDetails.color && (
                          <div className="variant-detail-item">
                            🎨 <strong>Couleur:</strong> {selectedOrder.variantDetails.color}
                          </div>
                        )}

                        {selectedOrder.variantDetails.material && (
                          <div className="variant-detail-item">
                            🔨 <strong>Matériau:</strong> {selectedOrder.variantDetails.material}
                          </div>
                        )}
                      </div>
                    )}

                    <p className="product-price">{formatPrice(selectedOrder.productPrice)} DA</p>
                  </div>
                </div>
              </div>

              {selectedOrder.notes && (
                <div className="detail-section">
                  <h3 className="detail-title">Notes</h3>
                  <p className="order-notes">{selectedOrder.notes}</p>
                </div>
              )}

              {/* Total */}
              <div className="detail-section order-total-section">
                <div className="order-total-row">
                  <span>Total:</span>
                  <span className="total-amount">{formatPrice(selectedOrder.total)} DA</span>
                </div>
              </div>
            </div>

            <div className="modal-footer">
              <button
                className="btn btn-primary"
                onClick={() => openStatusChange(selectedOrder, true)}
              >
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <polyline points="22 12 18 12 15 21 9 3 6 12 2 12" />
                </svg>
                Changer le statut
              </button>
              <button
                className="btn btn-secondary"
                onClick={() => setShowModal(false)}
              >
                Fermer
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Status Change Modal */}
      {showStatusModal && statusChangeOrder && (
        <div className="modal-overlay" onClick={() => setShowStatusModal(false)}>
          <div className="modal-content status-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Changer le statut</h2>
              <button className="modal-close" onClick={() => setShowStatusModal(false)}>
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <line x1="18" y1="6" x2="6" y2="18" />
                  <line x1="6" y1="6" x2="18" y2="18" />
                </svg>
              </button>
            </div>

            <div className="modal-body">
              <div className="status-change-info">
                <p>Commande: <strong>#{statusChangeOrder.orderNumber}</strong></p>
                <p>Client: <strong>{statusChangeOrder.fullName}</strong></p>
                <p>Statut actuel: <span className={`status-badge ${getStatusBadgeClass(statusChangeOrder.status)}`}>
                  {getStatusLabel(statusChangeOrder.status)}
                </span></p>
              </div>

              <div className="status-options">
                <h3>Sélectionner le nouveau statut:</h3>
                <div className="status-buttons-grid">
                  {getAvailableStatuses(statusChangeOrder.status).map(status => (
                    <button
                      key={status.value}
                      className={`status-btn ${status.value}`}
                      onClick={() => updateOrderStatus(status.value)}
                    >
                      {status.label}
                    </button>
                  ))}
                </div>
              </div>
            </div>

            <div className="modal-footer">
              <button
                className="btn btn-secondary"
                onClick={() => setShowStatusModal(false)}
              >
                Annuler
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminOrders;